import { useState, useEffect } from 'react';

/**
 * Custom hook to track the visual viewport height, specifically designed for mobile pages.
 *
 * This hook monitors changes to the visual viewport height, which is crucial for mobile
 * applications where the on-screen keyboard can dynamically change the available viewport.
 *
 * Key features:
 * - Tracks actual visible viewport height (excluding on-screen keyboard)
 * - Uses the Visual Viewport API when available for accurate mobile keyboard detection
 * - Automatically updates when viewport changes (keyboard appears/disappears, orientation changes)
 *
 * IMPORTANT: This hook is specifically designed for mobile pages where the on-screen
 * keyboard behavior needs to be tracked. F
 *
 * @returns {Object} Object containing the current visual viewport height
 * @returns {number} returns.visualViewportHeight - The current visual viewport height in pixels (rounded)
 */
const useVisualViewportHeight = () => {
  const [visualViewportHeight, setVisualViewportHeight] = useState(
    window.visualViewport ? window.visualViewport.height : window.innerHeight
  );

  useEffect(() => {
    const handleVisualViewportChange = () => {
      if (window.visualViewport) {
        setVisualViewportHeight(window.visualViewport.height);
      }
    };

    // Add event listeners

    // Use visualViewport API if available (for mobile keyboard detection)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportChange);
      window.visualViewport.addEventListener('scroll', handleVisualViewportChange);
    }

    // Cleanup event listeners on component unmount
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
        window.visualViewport.removeEventListener('scroll', handleVisualViewportChange);
      }
    };
  }, []);

  return {
    visualViewportHeight: Math.round(visualViewportHeight),
  };
};

export default useVisualViewportHeight;
